import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft, Upload, PenTool, Download, Loader2, Eye,
  CheckCircle2, Sparkles, X,
} from "lucide-react";
import { toast } from "sonner";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category badge colours
const CAT_COLORS = {
  personality:   "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40",
  emotional:     "bg-rose-500/15 text-rose-300 border-rose-400/40",
  mental:        "bg-indigo-500/15 text-indigo-300 border-indigo-400/40",
  social:        "bg-emerald-500/15 text-emerald-300 border-emerald-400/40",
  relationships: "bg-pink-500/15 text-pink-300 border-pink-400/40",
  career:        "bg-blue-500/15 text-blue-300 border-blue-400/40",
  creativity:    "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-400/40",
  intelligence:  "bg-cyan-500/15 text-cyan-300 border-cyan-400/40",
  health:        "bg-red-500/15 text-red-300 border-red-400/40",
  other:         "bg-neutral-500/15 text-neutral-300 border-neutral-400/40",
};

const CATEGORY_ORDER = [
  "personality", "emotional", "mental", "social", "relationships",
  "career", "creativity", "intelligence", "health", "other",
];

/**
 * Handwriting Analysis (Charisma tools family)
 *  - Upload a photo of handwriting -> browser preview + server-side Tesseract OCR
 *  - Select observed graphology features via checkboxes/radios (grouped)
 *  - Server matches selections + OCR keywords against a curated ruleset
 *  - Show categorised interpretation + one-click PDF download
 * 100% free, zero LLM credits used.
 */
export default function HandwritingPage() {
  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = useState({}); // key -> true
  const [writerName, setWriterName] = useState("");
  const [notes, setNotes] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const fileRef = useRef(null);

  // Fetch feature groups on mount
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/handwriting/rules`);
        setGroups(data.groups || []);
      } catch (e) {
        toast.error("Failed to load handwriting ruleset");
      }
    })();
  }, []);

  const totalSelected = useMemo(
    () => Object.values(selected).filter(Boolean).length,
    [selected]
  );

  // ----- image upload / preview -----
  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please choose an image file (JPG / PNG)");
      return;
    }
    setImageFile(f);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(f);
  };

  const clearImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  // ----- toggle a feature key -----
  const toggle = (groupKey, optionKey, isRadio = true) => {
    setSelected((s) => {
      const next = { ...s };
      if (isRadio) {
        // clear other keys in the same group first
        for (const k of Object.keys(next)) {
          if (k.startsWith(groupKey + ".")) delete next[k];
        }
        next[optionKey] = true;
      } else {
        // checkbox toggle
        if (next[optionKey]) delete next[optionKey];
        else next[optionKey] = true;
      }
      return next;
    });
  };

  const clearAll = () => {
    setSelected({});
    setResult(null);
  };

  // Which groups use radio (mutually exclusive) vs checkbox (multi-select)
  const MULTI_SELECT_GROUPS = new Set(["loops", "doodles", "strokes"]);

  // ----- run analysis -----
  const analyze = async () => {
    if (totalSelected === 0 && !imageFile) {
      toast.error("Select at least one feature or upload a handwriting image");
      return;
    }
    setAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append(
        "selections_json",
        JSON.stringify({
          selection_keys: Object.keys(selected),
          writer_name: writerName,
          notes,
        })
      );
      if (imageFile) fd.append("image", imageFile);
      const { data } = await axios.post(
        `${API}/handwriting/analyze`, fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setResult(data);
      // scroll to results
      setTimeout(() => {
        document.getElementById("hw-results")?.scrollIntoView({ behavior: "smooth" });
      }, 100);
      toast.success(`Analysis complete — ${data.matched_rules.length} traits interpreted`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };

  const downloadPdf = async () => {
    if (!result) return;
    setDownloadingPdf(true);
    try {
      const payload = {
        writer_name: result.writer_name || "",
        notes: result.notes || "",
        ocr_text: result.ocr_text || "",
        ocr_confidence: result.ocr_confidence || 0,
        matched_rules: result.matched_rules || [],
        categories: result.categories || {},
        summary: result.summary || "",
      };
      const r = await axios.post(
        `${API}/handwriting/report.pdf`, payload,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `handwriting-analysis-${(writerName || "report").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("PDF download failed");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const orderedCats = useMemo(() => {
    if (!result?.categories) return [];
    const keys = Object.keys(result.categories);
    return [
      ...CATEGORY_ORDER.filter((c) => keys.includes(c)),
      ...keys.filter((c) => !CATEGORY_ORDER.includes(c)),
    ];
  }, [result]);

  return (
    <div className="min-h-screen bg-[#0F1115] text-[#E6E1D6]">
      <div className="grain-overlay pointer-events-none fixed inset-0 z-0" />

      {/* Header */}
      <header className="relative z-10 border-b border-[#2A2D35] bg-[#0F1115]/90 backdrop-blur-md sticky top-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/"
                  className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-mono
                             text-[#A19E95] hover:text-[#D4AF37] transition-colors"
                  data-testid="handwriting-back-link">
              <ArrowLeft size={12} /> Dashboard
            </Link>
            <div className="h-6 w-px bg-[#2A2D35]" />
            <div className="min-w-0">
              <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.4em] text-[#6E6B64] font-mono">
                Astrology Studio
              </div>
              <div className="font-heading text-lg sm:text-xl text-[#E6E1D6] flex items-center gap-2">
                <PenTool size={18} className="text-[#D4AF37]" />
                Handwriting Analysis
              </div>
            </div>
          </div>
          <LanguageSwitcher />
        </div>
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Intro */}
        <div className="mb-6 border border-[#2A2D35] bg-[#151821] rounded-sm px-4 sm:px-6 py-4 sm:py-5">
          <div className="flex items-start gap-3">
            <Sparkles className="text-[#D4AF37] shrink-0 mt-0.5" size={16} />
            <div className="text-xs sm:text-sm text-[#A19E95] leading-relaxed">
              Upload a photo of the handwriting sample and tick the observed graphology features below.
              We run <span className="text-[#E6E1D6]">Tesseract OCR</span> on the image to extract any legible text,
              match your selections and OCR keywords against a curated set of{" "}
              <span className="text-[#D4AF37]">{groups.reduce((n, g) => n + g.options.length, 0)}+ rules</span>,
              and give you a full categorised interpretation.
              <span className="block mt-1 text-[#6E6B64] italic">100% free · zero AI credits used.</span>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* LEFT: image + writer info */}
          <div className="lg:col-span-1 space-y-4">
            <div className="border border-[#2A2D35] bg-[#151821] rounded-sm p-4">
              <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#6E6B64] mb-3">
                Handwriting sample
              </div>
              {imagePreview ? (
                <div className="relative">
                  <img src={imagePreview}
                       alt="handwriting"
                       className="w-full rounded-sm border border-[#2A2D35]"
                       data-testid="handwriting-image-preview" />
                  <button
                    onClick={clearImage}
                    className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white rounded-full p-1"
                    data-testid="handwriting-clear-image-btn">
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => fileRef.current?.click()}
                  className="w-full h-40 border-2 border-dashed border-[#2A2D35] hover:border-[#D4AF37]/50
                             rounded-sm flex flex-col items-center justify-center gap-2 text-[#6E6B64]
                             hover:text-[#D4AF37] transition-colors"
                  data-testid="handwriting-upload-dropzone">
                  <Upload size={22} />
                  <span className="text-xs">Click to upload a photo</span>
                  <span className="text-[10px] font-mono">JPG · PNG · optional</span>
                </button>
              )}
              <input ref={fileRef}
                     type="file"
                     accept="image/*"
                     onChange={onPickFile}
                     className="hidden"
                     data-testid="handwriting-file-input" />
            </div>

            <div className="border border-[#2A2D35] bg-[#151821] rounded-sm p-4 space-y-3">
              <div>
                <Label htmlFor="writer" className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#6E6B64]">
                  Writer's name (optional)
                </Label>
                <Input id="writer"
                       value={writerName}
                       onChange={(e) => setWriterName(e.target.value)}
                       className="mt-1 bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6]"
                       placeholder="e.g. Ahalya"
                       data-testid="handwriting-writer-name" />
              </div>
              <div>
                <Label htmlFor="notes" className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#6E6B64]">
                  Analyst notes (optional)
                </Label>
                <Textarea id="notes"
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          className="mt-1 bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6] min-h-[80px]"
                          placeholder="Anything noteworthy about the sample or the writer's context…"
                          data-testid="handwriting-notes" />
              </div>
            </div>

            <div className="border border-[#2A2D35] bg-[#151821] rounded-sm p-4 sticky top-24 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#6E6B64]">
                  Selections
                </div>
                <div className="text-lg font-heading text-[#D4AF37]" data-testid="handwriting-selection-count">
                  {totalSelected}
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={analyze}
                  disabled={analyzing}
                  className="flex-1 bg-[#D4AF37] text-[#0F1115] hover:bg-[#E5C158] font-semibold"
                  data-testid="handwriting-analyze-btn">
                  {analyzing ? (
                    <><Loader2 size={14} className="mr-2 animate-spin" /> Analyzing…</>
                  ) : (
                    <><Eye size={14} className="mr-2" /> Analyze</>
                  )}
                </Button>
                <Button
                  onClick={clearAll}
                  variant="outline"
                  className="border-[#2A2D35] text-[#A19E95] hover:text-[#D4AF37]"
                  data-testid="handwriting-clear-btn">
                  Reset
                </Button>
              </div>
              {result && (
                <Button
                  onClick={downloadPdf}
                  disabled={downloadingPdf}
                  variant="outline"
                  className="w-full border-[#2A2D35] text-[#E6E1D6] hover:text-[#D4AF37] hover:border-[#D4AF37]/40"
                  data-testid="handwriting-pdf-btn">
                  {downloadingPdf ? (
                    <><Loader2 size={14} className="mr-2 animate-spin" /> Building PDF…</>
                  ) : (
                    <><Download size={14} className="mr-2" /> Download PDF report</>
                  )}
                </Button>
              )}
            </div>
          </div>

          {/* RIGHT: feature groups */}
          <div className="lg:col-span-2 space-y-4">
            {groups.map((g) => {
              const isMulti = MULTI_SELECT_GROUPS.has(g.key);
              return (
                <div key={g.key}
                     className="border border-[#2A2D35] bg-[#151821] rounded-sm p-4"
                     data-testid={`handwriting-group-${g.key}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="text-sm font-heading text-[#E6E1D6]">{g.label}</div>
                      <div className="text-[10px] uppercase tracking-widest font-mono text-[#6E6B64]">
                        {isMulti ? "Multi-select" : "Choose one"} · {g.options.length} options
                      </div>
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-1.5">
                    {g.options.map((opt) => {
                      const active = !!selected[opt.selection_key];
                      return (
                        <button
                          key={opt.selection_key}
                          onClick={() => toggle(g.key, opt.selection_key, !isMulti)}
                          className={`text-left px-3 py-2 rounded-sm border transition-colors flex items-start gap-2
                            ${active
                              ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#E6E1D6]"
                              : "border-[#2A2D35] bg-[#0F1115] text-[#A19E95] hover:border-[#D4AF37]/40 hover:text-[#E6E1D6]"
                            }`}
                          data-testid={`handwriting-option-${opt.selection_key}`}>
                          <span className={`mt-0.5 inline-block w-3 h-3 shrink-0 border ${
                            active ? "bg-[#D4AF37] border-[#D4AF37]" : "border-[#2A2D35]"
                          } ${isMulti ? "" : "rounded-full"}`}>
                            {active && <CheckCircle2 size={12} className="text-[#0F1115] -m-px" />}
                          </span>
                          <span className="text-xs leading-tight">{opt.sign}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* RESULTS */}
        {result && (
          <div id="hw-results" className="mt-10 space-y-4">
            <div className="border border-[#D4AF37]/40 bg-[#151821] rounded-sm p-5">
              <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#D4AF37] mb-1">
                Analysis · {result.matched_rules.length} traits
              </div>
              <div className="text-sm text-[#E6E1D6] leading-relaxed" data-testid="handwriting-summary">
                {result.summary}
              </div>
              {result.ocr_text && (
                <div className="mt-4 border-t border-[#2A2D35] pt-3">
                  <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#6E6B64] mb-1">
                    OCR extracted text · confidence {Math.round(result.ocr_confidence * 100)}%
                  </div>
                  <div className="text-xs text-[#A19E95] italic whitespace-pre-wrap"
                       data-testid="handwriting-ocr-text">
                    “{result.ocr_text}”
                  </div>
                </div>
              )}
            </div>

            {orderedCats.map((cat) => {
              const rules = result.categories[cat] || [];
              const badge = CAT_COLORS[cat] || CAT_COLORS.other;
              return (
                <div key={cat}
                     className="border border-[#2A2D35] bg-[#151821] rounded-sm p-4"
                     data-testid={`handwriting-category-${cat}`}>
                  <div className={`inline-block text-[10px] uppercase tracking-[0.3em] font-mono
                                   px-2 py-1 border rounded-sm mb-3 ${badge}`}>
                    {cat} · {rules.length}
                  </div>
                  <div className="space-y-3">
                    {rules.map((r, i) => (
                      <div key={r.selection_key + "_" + i} className="border-l-2 border-[#2A2D35] pl-3">
                        <div className="text-xs uppercase tracking-widest font-mono text-[#6E6B64] mb-0.5">
                          {r.feature.replace(/_/g, " ")}
                          {r.matched_via === "ocr" && (
                            <span className="ml-2 text-[#A8896C]">· OCR keyword: {r.matched_keyword}</span>
                          )}
                        </div>
                        <div className="text-sm text-[#E6E1D6] font-medium">{r.sign}</div>
                        <div className="text-xs text-[#A19E95] mt-1 leading-relaxed">{r.meaning}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
